import React, { useEffect, useState } from 'react';
import { usePasswordlessAuth } from '../provider/PasswordlessAuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const PasswordlessDashboard = () => {
  const { user, loading, logout, authenticatedRequest, isAuthenticated } = usePasswordlessAuth();
  const navigate = useNavigate();
  const [testData, setTestData] = useState(null);
  const [loadingTest, setLoadingTest] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/passwordless-login');
    }
  }, [loading, isAuthenticated, navigate]);

  const testAuthenticatedCall = async () => {
    setLoadingTest(true);
    try {
      // Test calling an existing protected endpoint
      const response = await authenticatedRequest({
        method: 'get',
        url: '/api/auth/me'
      });
      setTestData(response.data);
    } catch (err) {
      console.error('Test call failed:', err);
      setTestData({ error: err.message });
    } finally {
      setLoadingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0fb592] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <img
                src="https://www.jumpstart-uk.com/wp-content/uploads/2022/05/jumpstart-logo-black.svg"
                className="h-8"
                alt="Jumpstart Logo"
              />
              <h1 className="ml-4 text-xl font-semibold text-gray-900">
                Passwordless Authentication Test
              </h1>
            </div>
            <button
              onClick={logout}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Welcome Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Welcome, {user?.firstName || user?.email}! 🎉
          </h2>
          <p className="text-gray-600 mb-6">
            You've successfully signed in using the new passwordless authentication system.
          </p>

          {/* User Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Your Account Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Email:</span>
                <span className="ml-2 text-gray-600">{user?.email}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Role:</span>
                <span className="ml-2 text-gray-600 capitalize">{user?.role}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Status:</span>
                <span className="ml-2 text-gray-600">{user?.status}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Last Login:</span>
                <span className="ml-2 text-gray-600">
                  {user?.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'N/A'}
                </span>
              </div>
              {user?.company && (
                <>
                  <div className="sm:col-span-2">
                    <span className="font-medium text-gray-700">Company:</span>
                    <span className="ml-2 text-gray-600">{user.company.name}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">New Authentication Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-gray-900">Passwordless Login</h4>
                <p className="text-sm text-gray-600">No passwords to remember - just use your email</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-gray-900">Secure Magic Links</h4>
                <p className="text-sm text-gray-600">24-hour expiring links with rate limiting</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-gray-900">Session Management</h4>
                <p className="text-sm text-gray-600">7-day sessions (30 days with "remember me")</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-gray-900">Audit Trail</h4>
                <p className="text-sm text-gray-600">Complete logging of authentication events</p>
              </div>
            </div>
          </div>
        </div>

        {/* Test API Call Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Test Authentication</h3>
          <p className="text-gray-600 mb-4">
            Test that your authentication token works with protected API endpoints.
          </p>
          
          <button
            onClick={testAuthenticatedCall}
            disabled={loadingTest}
            className="bg-[#0fb592] text-white px-4 py-2 rounded-lg hover:bg-[#0da578] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loadingTest ? 'Testing...' : 'Test API Call'}
          </button>

          {testData && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 mb-2">API Response:</h4>
              <pre className="text-xs text-gray-600 overflow-auto">
                {JSON.stringify(testData, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="mt-8 text-center space-y-4">
          <p className="text-gray-600 mb-4">
            Ready to explore the main application?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/browse-candidates')}
              className="bg-gray-900 text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
            >
              Go to Main Dashboard
            </button>
            <button
              onClick={() => navigate('/admin/user-management')}
              className="bg-[#0fb592] text-white px-6 py-3 rounded-lg hover:bg-[#0da578] transition-colors"
            >
              Admin: User Management
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordlessDashboard; 